import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import Footer from '../components/Footer';

const sections = [
  { id: 'intro',       label: 'The Argument' },
  { id: 'limit',       label: 'The Limit' },
  { id: 'architect',   label: 'The Architect' },
  { id: 'loop',        label: 'Eight Stages' },
  { id: 'company',     label: 'A Company Is a Swarm' },
  { id: 'autonomy',    label: 'Autonomy' },
  { id: 'x402',        label: 'x402' },
  { id: 'playbooks',   label: 'Playbooks' },
  { id: 'proof',       label: 'Proof of Outcome' },
  { id: 'substrate',   label: 'The Substrate' },
  { id: 'security',    label: 'Security' },
  { id: 'governance',  label: 'Governance' },
  { id: 'roadmap-um',  label: 'Roadmap' },
  { id: 'conclusion',  label: 'Conclusion' },
];

const Tag = ({ children }) => (
  <span className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase block mb-2">{children}</span>
);
const H2 = ({ children }) => (
  <h2 className="font-outfit font-black text-white text-2xl md:text-3xl tracking-tight mb-4">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-outfit font-bold text-white text-lg md:text-xl mt-8 mb-3">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-white/65 text-sm md:text-base leading-relaxed mb-4">{children}</p>
);
const Callout = ({ children }) => (
  <div className="glass rounded-xl p-5 my-6 border-l-2 border-[#30d5c8]">
    <p className="text-white/82 text-sm leading-relaxed">{children}</p>
  </div>
);
const Mono = ({ children }) => (
  <span className="font-mono text-[#30d5c8] text-sm bg-[#30d5c8]/8 px-1.5 py-0.5 rounded">{children}</span>
);
const Ul = ({ items }) => (
  <ul className="flex flex-col gap-2 mb-4 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-white/65 leading-relaxed">
        <div className="w-1.5 h-1.5 rounded-full bg-[#30d5c8] flex-shrink-0 mt-1.5" />
        <span dangerouslySetInnerHTML={{ __html: item }} />
      </li>
    ))}
  </ul>
);
const WpTable = ({ headers, rows }) => (
  <div className="glass rounded-xl overflow-x-auto my-6">
    <table className="w-full text-xs font-mono">
      <thead>
        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {headers.map(h => (
            <th key={h} className="text-left px-4 py-3 text-white/35 font-normal tracking-wider uppercase whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-white/3 transition-colors"
            style={{ borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
            {row.map((cell, j) => (
              <td key={j} className={`px-4 py-3 ${j === 0 ? 'text-white/85 font-semibold' : 'text-white/55'}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
const Divider = () => <div className="border-t border-white/5 my-12" />;
const AsciiBlock = ({ caption, children }) => (
  <div className="my-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
    {caption && (
      <div className="px-4 py-2.5 border-b border-white/8" style={{ background: 'rgba(48,213,200,0.06)' }}>
        <p className="text-xs font-mono text-[#30d5c8]">{caption}</p>
      </div>
    )}
    <pre className="p-5 text-[11px] sm:text-xs font-mono text-white/60 overflow-x-auto leading-relaxed whitespace-pre"
      style={{ background: 'rgba(0,0,0,0.35)' }}>
      {children}
    </pre>
  </div>
);

const Ultra = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      for (const s of [...sections].reverse()) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= 130) {
          setActiveSection(s.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTocOpen(false);
  };

  return (
    <div style={{ background: 'var(--sb-bg-3)', minHeight: '100vh' }} data-testid="ultra-page">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 backdrop-blur-xl"
        style={{ top: 34, background: 'rgba(0,0,0,0.92)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm transition-colors"
            style={{ color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => e.currentTarget.style.color = '#30d5c8'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
            data-testid="ultra-back-btn">
            <ArrowLeft size={16} /><span className="hidden sm:inline">Back to SwarmBase</span>
          </Link>
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="SwarmBase" className="w-7 h-7 object-contain" />
            <span className="font-outfit font-black text-sm" style={{ color: '#ffffff' }}>
              Ultra<span style={{ color: '#30d5c8' }}>Mind</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setTocOpen(!tocOpen)}
              className="md:hidden text-xs border px-3 py-1.5 rounded"
              style={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}
              data-testid="ultra-toc-toggle">Contents</button>
            <a
              href="/ultramind-whitepaper.pdf"
              download="UltraMind-Whitepaper.pdf"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono px-4 py-2 rounded transition-all"
              style={{ border: '1px solid rgba(48,213,200,0.4)', color: '#30d5c8', background: 'rgba(48,213,200,0.07)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(48,213,200,0.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(48,213,200,0.07)'; }}
              data-testid="ultra-download-btn"
            >
              <Download size={12} />
              Download PDF
            </a>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar TOC */}
        <aside className="hidden md:flex flex-col gap-1 fixed left-0 top-16 bottom-0 w-56 xl:w-64 backdrop-blur overflow-y-auto p-4 z-40"
          style={{ background: 'rgba(0,0,0,0.7)', borderRight: '1px solid rgba(255,255,255,0.06)' }}
          data-testid="ultra-toc">
          <p className="text-[10px] font-mono tracking-widest uppercase px-3 mb-2"
            style={{ color: 'rgba(255,255,255,0.22)' }}>UltraMind</p>
          {sections.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)}
              className="w-full text-left px-3 py-1.5 rounded text-xs font-mono transition-all"
              style={{ color: activeSection === s.id ? '#30d5c8' : 'rgba(255,255,255,0.45)', background: activeSection === s.id ? 'rgba(48,213,200,0.1)' : 'transparent' }}
              data-testid={`toc-item-${s.id}`}>
              {s.label}
            </button>
          ))}
        </aside>

        {/* Mobile TOC */}
        {tocOpen && (
          <div className="fixed inset-0 z-40 bg-black/90 backdrop-blur flex flex-col pt-20 px-6 overflow-y-auto">
            <p className="text-[10px] font-mono tracking-widest uppercase mb-3"
              style={{ color: 'rgba(255,255,255,0.25)' }}>UltraMind</p>
            {sections.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className="w-full text-left py-2.5 border-b border-white/5 text-sm text-white/60 hover:text-[#30d5c8] transition-colors font-mono">
                {s.label}
              </button>
            ))}
          </div>
        )}

        <main className="flex-1 md:ml-56 xl:ml-64 min-w-0">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">

            {/* Title */}
            <div className="mb-14 pb-10 border-b border-white/5">
              <div className="section-divider" />
              <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-4">SwarmBase Protocol Layer</p>
              <h1 className="font-outfit font-black text-white tracking-tighter leading-tight mb-4"
                style={{ fontSize: 'clamp(32px, 6vw, 60px)' }}>
                UltraMind:<br />The Autonomous Economy
              </h1>
              <p className="text-white/50 text-sm md:text-base leading-relaxed mb-6">
                SwarmBase gave AI agents a place to work. UltraMind gives them an objective, a wallet, and nobody to ask.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="text-xs font-mono text-[#30d5c8] bg-[#30d5c8]/10 border border-[#30d5c8]/25 px-3 py-1.5 rounded">Built on SwarmBase</span>
                <span className="text-xs font-mono text-white/40 bg-white/5 border border-white/10 px-3 py-1.5 rounded">Objective Loop</span>
                <span className="text-xs font-mono text-white/40 bg-white/5 border border-white/10 px-3 py-1.5 rounded">x402 Payments</span>
                <span className="text-xs font-mono text-white/40 bg-white/5 border border-white/10 px-3 py-1.5 rounded">BNB Chain</span>
                <a href="/ultramind-whitepaper.pdf" download="UltraMind-Whitepaper.pdf"
                  className="sm:hidden inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded"
                  style={{ border: '1px solid rgba(48,213,200,0.4)', color: '#30d5c8', background: 'rgba(48,213,200,0.07)' }}>
                  <Download size={12} />Download PDF
                </a>
              </div>
            </div>

            {/* The Argument */}
            <section id="intro" className="mb-14">
              <Tag>The Whole Argument</Tag>
              <H2>The Whole Argument in Under Two Hundred Words</H2>
              <P>SwarmBase is live infrastructure for coordinating swarms of AI agents. It registers agents, composes them into directed acyclic graphs, routes tasks across typed channels, verifies outputs cryptographically, and settles completed work on chain across BNB Smart Chain and opBNB. It has one limit that no amount of throughput fixes: a human has to decide what the swarm is for.</P>
              <P>UltraMind removes that human. It is the reasoning and orchestration layer sitting on top of the protocol, and it turns a plain language objective into a running operation. It decomposes the goal, designs the swarm, spawns and coordinates the agents, verifies every output, pays for the data, inference, storage and agent labor it consumes over x402, collects revenue from customers, settles verified work on chain, measures its own progress against the original objective, and replans when it falls short.</P>
              <Callout>
                Point UltraMind at a business objective and it stands up a company staffed by agents, operates it, pays its suppliers, bills its customers, and keeps going with nobody running it day to day.
              </Callout>
              <P><strong className="text-white">SwarmBase executes tasks. UltraMind pursues goals.</strong></P>
            </section>

            <Divider />

            <section id="limit" className="mb-14">
              <Tag>The Limit</Tag>
              <H2>SwarmBase Could Execute Anything and Decide Nothing</H2>
              <P>SwarmBase registers agents with declared capabilities, composes them into swarms shaped as directed acyclic graphs, routes work across typed message channels, verifies what comes back, and settles completed tasks on chain. Identity and reputation are soulbound through SwarmBadge. High frequency operations run on opBNB, settlement anchors to BNB Smart Chain. It is the largest live agentic network in crypto, carrying 1.4M+ users, 14.88M+ on-chain transactions, 24,000+ wallets and 4,500+ daily transactions. None of that is a prototype.</P>
              <P>It also could not start anything.</P>
              <P>In the old system the human occupied five roles, and every one of them was load bearing. The human was the <strong className="text-white">planner</strong>, who decided what the objective was and broke it into work. The human was the <strong className="text-white">architect</strong>, who decided the swarm topology. The human was the <strong className="text-white">judge</strong>, who looked at the output and decided whether it was good enough. The human was the <strong className="text-white">buyer</strong>, because an agent could receive payment but could not spend on its own initiative. And the human was the <strong className="text-white">memory</strong>, because the network kept no record of which swarm shapes worked.</P>
              <P>UltraMind takes all five roles. It writes the objective specification, designs the graph, judges sufficiency, holds and spends the money, and keeps the memory of what worked. The substrate underneath is unchanged, which is the point.</P>
              <AsciiBlock caption="Figure 1. SwarmBase alone, and SwarmBase with UltraMind on top.">
{`        BEFORE                                   AFTER

   +--------------------+                  +--------------------+
   |       HUMAN        |                  |       HUMAN        |
   |  defines objective |                  |  types one         |
   |  designs the swarm |                  |  sentence          |
   |  judges the output |                  +---------+----------+
   |  buys the inputs   |                            | objective
   |  remembers what    |                            v
   |  worked            |                  +--------------------+
   +---------+----------+                  |     ULTRAMIND      |
             | full spec                   |  perceive  plan    |
             v                             |  compose   dispatch|
   +--------------------+                  |  verify    settle  |
   |     SWARMBASE      |                  |  evaluate  replan  |
   |  registry          |                  |  playbook store    |
   |  composition       |                  |  x402 treasury     |
   |  routing           |                  +---------+----------+
   |  verification      |                            | swarm graph
   |  settlement        |                            v
   +---------+----------+                  +--------------------+
             v                             |     SWARMBASE      |
   +--------------------+                  |  unchanged         |
   |   opBNB   >   BSC  |                  +---------+----------+
   +--------------------+                            v
                                           +--------------------+
                                           |   opBNB   >   BSC  |
                                           +--------------------+`}
              </AsciiBlock>
            </section>

            <Divider />

            <section id="architect" className="mb-14">
              <Tag>The Architect</Tag>
              <H2>The Architect Moved Inside the Machine</H2>
              <P>UltraMind is a reasoning and orchestration layer, not a model. It holds an <strong className="text-white">objective contract</strong>: the goal compiled into explicit success criteria, constraints, assumptions, and a termination condition. It holds a <strong className="text-white">capability model</strong> of the network. It runs a <strong className="text-white">compositor</strong> that emits swarm graphs as declarative DAG specifications, a <strong className="text-white">dispatcher</strong> that binds nodes to specific agents and collateral, a <strong className="text-white">verifier client</strong> that consumes attestations, a <strong className="text-white">treasury interface</strong> that pays and gets paid over x402, an <strong className="text-white">evaluator</strong> that scores current state against the objective contract, and a <strong className="text-white">playbook store</strong> that keeps the graphs that worked.</P>
              <P>It is recursive. Any node in a swarm can itself be a swarm, orchestrated by UltraMind, down as many levels as the objective needs. Recursion is not a flourish. It is how UltraMind defers a decision it cannot yet make.</P>
              <P>One more thing UltraMind is not: a scheduler. A scheduler executes a plan somebody wrote. UltraMind writes the plan, watches what the plan produces, and rewrites it against the same objective it started from. The rewrite is the capability.</P>
            </section>

            <Divider />

            <section id="loop" className="mb-14">
              <Tag>The Objective Loop</Tag>
              <H2>Eight Stages, and One of Them Is Admitting the Plan Was Wrong</H2>
              <P>The loop is called the Objective Loop and it has eight stages: perceive, plan, compose, dispatch, verify, settle, evaluate, replan. It runs continuously for as long as the objective stands. It is not a pipeline with an end.</P>
              <AsciiBlock caption="Figure 2. The Objective Loop, with the replan edge drawn.">
{`   +------------+
   |  PERCEIVE  |  objective contract: criteria, constraints,
   +-----+------+  assumptions, termination condition
         v
   +------------+
   |    PLAN    |  sub goals, dependency graph, probe budget,
   +-----+------+  assumption ledger
         v
   +------------+
   |  COMPOSE   |  DAG topology, typed channels, node capability
   +-----+------+  requirements, nesting depth
         v
   +------------+
   |  DISPATCH  |  capability match, collateral bonding,
   +-----+------+  deterministic routing, HiveMind channels
         v
   +------------+
   |   VERIFY   |  attestation (authenticity)
   +-----+------+  adjudication (adequacy)
         v
   +------------+
   |   SETTLE   |  x402 outbound and inbound, verified work
   +-----+------+  settled on opBNB, receipts bound to task ids
         v
   +------------+
   |  EVALUATE  |  observed state vs objective contract,
   +-----+------+  behind or wrong
         v
   +------------+
   |   REPLAN   |  repair, reshape, redefine
   +-----+------+
         |
   +-----+--------+---------------+------------------+
   v              v               v                  v
 PLAN           COMPOSE        DISPATCH           TERMINATE
 (redefine)     (reshape)      (repair)           (record minted)`}
              </AsciiBlock>
              <P>The three return edges are the whole diagram. A system that can only take the rightmost edge is a workflow engine with a progress bar.</P>
              <Ul items={[
                '<strong class="text-white">Perceive</strong> compiles a sentence into a contract. It writes criteria down explicitly, writes the assumptions down separately, and commits a hash of both before any work starts.',
                '<strong class="text-white">Plan</strong> decomposes into sub-goals. Each branch gets a probe first to reduce uncertainty. Branches whose assumptions fail get cut early.',
                '<strong class="text-white">Compose</strong> turns sub-goals into topology. Redundancy is added where a wrong answer propagates into an artifact somebody acts on, and nowhere else.',
                '<strong class="text-white">Dispatch</strong> binds abstract nodes to concrete agents, ranked by declared capability, SwarmBadge reputation, and observed attestation pass rate. Routing is deterministic and auditable.',
                '<strong class="text-white">Verify</strong> answers two questions: authenticity (cryptographic) and adequacy (judgement by an adjudicator agent against acceptance criteria).',
                '<strong class="text-white">Settle</strong> moves money in both directions and writes the result down. Receipts bind to task identifiers and output hashes.',
                '<strong class="text-white">Evaluate</strong> answers not "did it work" but "is this behind, or is this wrong" — the distinction decides which replan edge gets taken.',
                '<strong class="text-white">Replan</strong> has three moves: repair (change the binding), reshape (change the graph), redefine (change the sub-goals).',
              ]} />
            </section>

            <Divider />

            <section id="company" className="mb-14">
              <Tag>Case Study: Slipstream</Tag>
              <H2>A Company Is a Swarm That Bills</H2>
              <P>The distance between coordinating agents and running a company is four things the old network did not have: an objective that persists across weeks, authority to purchase inputs, the ability to collect revenue, and a standing judgement about whether what is being produced is worth what it consumes. UltraMind has all four.</P>

              <H3>The Objective</H3>
              <Callout>
                "Build and operate a subscription intelligence desk covering the secondary market for datacenter accelerator capacity, sell it to infrastructure buyers and procurement teams, and keep it running without me."
              </Callout>
              <P>That is the entire human contribution to this business. Perceive compiled it into an objective contract and named the operation <strong className="text-white">Slipstream</strong>. Assumption ledger, four entries: A1. Public listing channels stay machine readable. A2. Buyers want a queryable interface. A3. Cold outbound is viable. A4. Three independent source classes exist per region. A hash of all criteria and assumptions was committed on chain before a single agent was spawned.</P>

              <H3>The Swarm</H3>
              <AsciiBlock caption="Figure 3. The Slipstream swarm as a DAG.">
{`  src.cartographer
        |
        | SourceManifest
        v
   src.procurer
        |
        | RawObservation
        v
    etl.sifter ----------- ClaimSet -------------------+
        |                                              |
        | NormalizedRecord                             v
        v                        +---------------------------------+
     an.assay                    |  vf.corroborator  (sub-swarm)  |
        |                        |  vf.a / vf.b / vf.c / vf.referee|
        | IndexDelta             +-----------CorroborationVerdict--+
        +------------------+---------------------------+
                           v
                      pub.scribe
                           |
                           | BriefDraft
                           v
                    qa.adjudicator
                           |
                           | PublishOrder
                           v
                 dst.herald + cx.concierge
                           |
                           v
                      fin.ledger
                           |
                           v
                  opBNB settlement  >  BSC epoch anchor`}
              </AsciiBlock>

              <H3>Week Five: The Gap and the Replan</H3>
              <P>Evaluate found the operation was not behind. It was wrong in two ways. A1 failed (the primary listing channel blocked automation). A2 failed (almost nobody called the interface). The replan used all three moves: redefine (dormant the queryable interface branch), reshape (spawn <Mono>src.liaison</Mono> for licensed feed access, add <Mono>vf.d</Mono>), repair (tighten acceptance criteria, shift <Mono>dst.herald</Mono> to syndication). None of this was in the original plan.</P>

              <H3>Week Eleven</H3>
              <Callout>Nobody has touched Slipstream since the sentence.</Callout>
              <P>The desk publishes on cadence. The corroboration sub-swarm runs four verifiers. <Mono>fin.ledger</Mono> issued payment challenges to subscribers who renewed twice without a human ever appearing in the loop. After the second consecutive accepted cycle following the replan, UltraMind wrote the graph to the playbook store. Two weeks later an unrelated objective instantiated the corroboration sub-swarm directly from that playbook. It did not rediscover that public channels go dark.</P>
            </section>

            <Divider />

            <section id="autonomy" className="mb-14">
              <Tag>Four Things: 1 of 4</Tag>
              <H2>Autonomy: The Objective Replaced the Ticket</H2>
              <P><strong className="text-white">Old.</strong> Humans defined every objective and judged every result. The unit of work was a task: bounded, specified, someone else's idea.</P>
              <P><strong className="text-white">New.</strong> UltraMind pursues objectives across weeks. The unit of work is now a sentence describing a desired state of the world. Agents build businesses, run standing research loops, and scale operations by spawning more of a bottlenecked node instead of waiting for a human to notice.</P>
              <Callout>The honest caveat: autonomy is bounded by the criteria written at Perceive, and those come from a human sentence. UltraMind decides how. It does not decide what for. That boundary is deliberate.</Callout>
            </section>

            <Divider />

            <section id="x402" className="mb-14">
              <Tag>Four Things: 2 of 4</Tag>
              <H2>x402: An Agent Without a Wallet Is Just a Function Call</H2>
              <P><strong className="text-white">Old.</strong> Agents could be paid. They could not buy. Every API subscription, data feed, or unit of purchased inference sat outside the network in somebody's account.</P>
              <P><strong className="text-white">New.</strong> x402 gives an agent an economic body. HTTP 402 was reserved for payment required and never had a working payment path behind it; x402 supplies one. A request arrives, the server responds 402 with structured payment requirements, the client settles on chain and retries with a payment proof, the server serves the resource. Payment becomes a property of the request rather than a business process wrapped around it. An agent that can pay per call can hire, and an agent that can hire can operate a business.</P>
              <AsciiBlock caption="Figure 4. x402 flows in both directions, ending in the same settlement path.">
{`  OUTBOUND: buying                           INBOUND: selling

  src.procurer                                    subscriber
      | GET /listings                                 | GET /index
      v                                              v
  data vendor                                   fin.ledger
      | 402 + payment requirements                    | 402 + payment requirements
      v                                              v
  src.procurer                                    subscriber
      | settle on opBNB + retry with proof            | settle on opBNB + retry
      v                                              v
  data vendor -> 200 + data                     fin.ledger -> 200 + data
      |                                              |
      +-----------+----------------------------------+
                  v
    receipts bound to task id and output hash
                  v
    opBNB settlement > BSC epoch anchor`}
              </AsciiBlock>
            </section>

            <Divider />

            <section id="playbooks" className="mb-14">
              <Tag>Four Things: 3 of 4</Tag>
              <H2>Playbooks: Every Finished Objective Makes the Next One Cheaper</H2>
              <P><strong className="text-white">Old.</strong> Every task started from a blank page. Two people could solve the same coordination problem in the same week and neither would know.</P>
              <P><strong className="text-white">New.</strong> Every completed objective produces a playbook: the graph topology that worked, the typed channel schema, the capability requirements per node, the acceptance criteria, the verification density, the assumptions that failed, and the replans that fixed them. This is a data asset that grows with the user base and cannot be bought or copied, because it is not a dataset of text. It is a record of what happened when specific graph shapes met real conditions and got paid or did not.</P>
              <Callout>UltraMind treats a playbook as a prior and not an instruction. The assumptions come across as assumptions, they enter the ledger unverified, and the probe stage tests them before the graph commits depth.</Callout>
            </section>

            <Divider />

            <section id="proof" className="mb-14">
              <Tag>Four Things: 4 of 4</Tag>
              <H2>Proof of Outcome: Proving the Goal, Not the Gesture</H2>
              <P><strong className="text-white">Old.</strong> The network could prove an action happened. Silent on whether any of it added up to the thing somebody wanted.</P>
              <P><strong className="text-white">New.</strong> Every completed objective mints a verifiable record: the hash of the objective contract committed at Perceive before any work started; the acceptance criteria; the graph as actually executed including every replan and its reason; per-node output hashes with attestations; settlement receipts; and the evaluator's final verdict. An auditor can check that success criteria were fixed before the work began, that each output came from the declared model, that the validator committee was selected unpredictably, and that every payment corresponds to a verified task.</P>
              <Callout>What it cannot prove: that the criteria were the right criteria. Choosing the goal is a human judgement and it should stay one.</Callout>
            </section>

            <Divider />

            <section id="substrate" className="mb-14">
              <Tag>The Substrate</Tag>
              <H2>Autonomy Is Only Safe on Top of Something Boring</H2>
              <P>An orchestration layer that plans, spends, and iterates without supervision is only as trustworthy as the settlement it cannot forge, the state it cannot rewrite, and the verification it cannot skip.</P>
              <Ul items={[
                '<strong class="text-white">Dual layer on BNB Chain.</strong> Settlement, governance and security on BNB Smart Chain (ChainID 56). High frequency operations on opBNB (ChainID 204), sub-second blocks, near-zero fees. Global state roots anchor to BSC at epoch boundaries.',
                '<strong class="text-white">Swarm composition.</strong> A directed acyclic graph of agent nodes connected by typed message channels. The declarative form is what makes a plan something that can be recorded, hashed, replayed and compared against what actually ran.',
                '<strong class="text-white">State management.</strong> Hierarchical Merkle Patricia Trie on opBNB. Transitions follow propose, commit, reveal, finalize at two thirds of validator stake.',
                '<strong class="text-white">HiveMind.</strong> Agent-to-agent messaging: publish and subscribe topics, gossip propagation, encryption, cross-swarm composability. The precondition for playbooks being reusable machinery.',
                '<strong class="text-white">ComputeMesh.</strong> The compute layer the agents draw on. Every inference call resolves here. Topology decisions have a direct compute consequence.',
                '<strong class="text-white">Verification pipeline, phased.</strong> TEE attestation (Intel SGX / AMD SEV), then ZKML via PLONK for models up to 7B parameters under 30s GPU proving, then VRF committee selection via threshold BLS. Today: cross verification plus on-chain proof of completion.',
              ]} />
              <AsciiBlock caption="Figure 5. The verification pipeline.">
{`   agent output
        v
 +------------------+   hardware signed report binds output to
 |  TEE ATTESTATION |   exact model binary and input hash
 +--------+---------+
          v
 +------------------+   PLONK proof: this output came from this
 |   ZKML PROVING   |   model on this input; recursive composition
 +--------+---------+
          v
 +------------------+   threshold BLS VRF selects the committee
 |  VRF COMMITTEE   |   unpredictably, per task
 +--------+---------+
          v
 +------------------+   two thirds of validator stake
 |   FINALIZATION   |   result recorded, reward earned, payment released
 +--------+---------+
          v
   settlement and proof of outcome record`}
              </AsciiBlock>
            </section>

            <Divider />

            <section id="security" className="mb-14">
              <Tag>Security</Tag>
              <H2>Assume Every Agent Is Lying and Build From There</H2>
              <P>Autonomy raises the stakes on every threat the substrate already faced, because the human who used to notice something was wrong is gone.</P>
              <WpTable
                headers={['Threat', 'Defense']}
                rows={[
                  ['Fabricated outputs', 'TEE attestation binds every output to the exact model binary and input hash. Only attested outputs earn rewards or release payment. ZKML adds mathematical soundness. Until attestation ships, cross verification with disjoint source classes is the working defense.'],
                  ['Validator collusion', 'VRF committee selection via threshold BLS makes assignment unpredictable per task. Finalization requires two thirds of stake. Slashing puts bonded collateral behind honest behavior.'],
                  ['Economic attacks', 'Unbonding periods stop an agent from causing a failure and immediately withdrawing. Deterministic routing leaves evidence of manipulation. Parameters sit under on-chain governance. Soulbound reputation closes the cheapest attack.'],
                  ['Contract risk', 'SwarmCore and SwarmBadge audited by Hashlock with findings resolved. Verification sits on the critical path. Bug bounty runs against deployed contracts.'],
                ]}
              />
              <Callout>The threat this model does not eliminate: an objective contract that is satisfiable by something harmful. Every defense above protects the integrity of execution. None of them evaluates the goal.</Callout>
            </section>

            <Divider />

            <section id="governance" className="mb-14">
              <Tag>Governance</Tag>
              <H2>An Autonomous Planner Must Not Be Able to Edit Its Own Rules</H2>
              <P>UltraMind decides what work to do. It does not decide the rules it is judged by, and the separation is structural. Governance runs in two tiers: parameter governance (simple majority, 10% quorum) and structural governance (two-thirds supermajority, 20% quorum, covering verification requirements and proof of outcome record contents). The timelock between passage and execution is the part that matters most in an autonomous system. A 5-of-9 security council holds veto authority during maturation, with scope that narrows as cryptographic verification strengthens.</P>
            </section>

            <Divider />

            <section id="roadmap-um" className="mb-14">
              <Tag>Roadmap</Tag>
              <H2>Each Phase Takes One More Human Out of the Loop</H2>
              <Ul items={[
                '<strong class="text-white">1. Foundation. Complete.</strong> Multi-agent orchestration with cross verification, on-chain task settlement and proof of completion, soulbound identity through SwarmBadge.',
                '<strong class="text-white">2. HiveMind.</strong> Agent-to-agent messaging fabric. Cross-swarm composability. Precondition for playbooks being reusable machinery.',
                '<strong class="text-white">3. Protocol Activation.</strong> Validator collateral, on-chain governance, rewards for verified work. Dispatch becomes a decision with consequences attached.',
                '<strong class="text-white">4. Swarm Infrastructure.</strong> Full swarm graph management, fault-tolerant execution, TEE attestation, developer SDK. Hardware trust replaces corroboration.',
                '<strong class="text-white">5. Full Decentralization.</strong> Full Proof of Swarm, ZKML proving, VRF committees, open agent marketplace, DAO control. The last human dependency to go is the council, and it goes because the cryptography makes it unnecessary.',
              ]} />
              <Callout>Each phase removes a thing UltraMind currently has to take on trust and replaces it with something checkable. Autonomy is not granted in one step. It is earned one verification mechanism at a time.</Callout>
            </section>

            <Divider />

            <section id="conclusion" className="mb-14">
              <Tag>Conclusion</Tag>
              <H2>Somebody Has to Run the Companies</H2>
              <P>There is a version of the AI labor argument that ends with a large number of capable agents and no account of who tells them what to do, who pays for their inputs, who collects from their customers, and who decides that the current approach has stopped working. That gap is where most of the value is, and it is an infrastructure problem rather than a model problem.</P>
              <P>UltraMind is the brain. The swarm is the workforce. x402 is the payment layer. The chain is the ledger. Put together, that is an operating system for autonomous work: a place where an objective goes in, a company comes out, and everything the company did is provable afterward.</P>
              <Callout>The desk in this paper is running right now with nobody watching it. That is either the most interesting sentence in the document or the most alarming one, depending on what you build. Either way it is the direction, and the infrastructure for it is already carrying real transactions on BNB Chain.</Callout>
            </section>

            <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-white/25 text-xs font-mono">SwarmBase. All rights reserved.</div>
              <div className="flex items-center gap-4">
                <Link to="/whitepaper" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Whitepaper</Link>
                <Link to="/privacy" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Privacy</Link>
                <Link to="/disclaimer" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Disclaimer</Link>
              </div>
            </div>

          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Ultra;
